import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import { ICustomSeriesPaneRenderer, PaneRendererCustomData, PriceToCoordinateConverter } from 'lightweight-charts'
import { GroupedBarsData, GroupSeriesHoveredData } from './data'
import { GroupedBarsSeriesOptions } from './options'
import { positionsBox, positionsLine } from './positions'

interface SingleBar {
    x: number;
    y: number;
    color: string;
}

interface GroupedBarsBarItem {
    singleBars: SingleBar[];
    singleBarWidth: number;
    hoveredBar: number[]
}

export class GroupedBarsSeriesRenderer<TData extends GroupedBarsData>
    implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<number, TData> | null = null
    _options: GroupedBarsSeriesOptions | null = null

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter,
        isHovered: boolean,
        hitTestData?: unknown
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<number, TData>,
        options: GroupedBarsSeriesOptions
    ): void {
        this._data = data
        this._options = options
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }
        const options = this._options
        let barWidth = options.barWidth * this._data.barSpacing
        const hoveredData: GroupSeriesHoveredData | undefined = (Number.isNaN(options.hoveredData.hoveredTime) || Number.isNaN(options.hoveredData.hoveredBar)) ? undefined : options.hoveredData
        const groups: GroupedBarsBarItem[] = this._data.bars.map((bar) => {
            const count = bar.originalData.values.length
            const time = options.chartApi?.timeScale().coordinateToTime(bar.x)
            const extraWidth: boolean = (options.increaseWidthOnHover && (time === hoveredData?.hoveredTime))
            // const hoveredBar: number[] = hoveredData ? hoveredData!.hoveredBar : []
            const singleBarWidth = barWidth / (count + 1)
            // const leftSpace: number = (extraWidth && hoveredBar.length>0 && hoveredBar < Math.floor(count / 2)) ? 0 : (singleBarWidth / 2)
            const leftSpace: number = (singleBarWidth / 2)
            const startX = bar.x + leftSpace - (barWidth / 2)
            return {
                singleBarWidth,
                singleBars: bar.originalData.values.map((value, index) => ({
                    y: priceToCoordinate(value) ?? 0,
                    color: options.colors[index % options.colors.length],
                    x: startX + (index * singleBarWidth) + 0
                })),
                hoveredBar: extraWidth ? (hoveredData?.hoveredBar ?? []) : []
            }
        })


        const zeroY = priceToCoordinate(0) ?? 0
        for (
            let i = this._data.visibleRange.from;
            i < this._data.visibleRange.to;
            i++
        ) {
            const group = groups[i]
            let lastX: number
            group.singleBars.forEach((bar, index) => {
                const extraWidth: boolean = group.hoveredBar.includes(index)
                const yPos = positionsBox(
                    zeroY,
                    bar.y,
                    renderingScope.verticalPixelRatio
                )
                const xPos = positionsLine(
                    bar.x,
                    renderingScope.horizontalPixelRatio,
                    ((extraWidth) ? 1.5 : 1) * group.singleBarWidth
                )
                renderingScope.context.beginPath()
                renderingScope.context.fillStyle = bar.color
                const offset = lastX ? xPos.position - lastX : 0
                renderingScope.context.fillRect(
                    xPos.position - offset - (extraWidth ? (group.singleBarWidth/2) * devicePixelRatio : 0),
                    yPos.position,
                    xPos.length,
                    yPos.length
                )
                lastX = xPos.position + xPos.length
            })
        }
    }

}